// Поиск, фильтры и сортировка журнала в адресе страницы (FR-035; T112, T141; contracts/api.md,
// GET /api/audit): поиск по ФИО автора и названию записи, автор, операция, запись, записи о
// пользователе и его назначениях, период, результат, сортировка по времени. Период — даты
// в поясе установки; в запрос уходят моменты начала и конца дня.
import { auditSortFields } from '@kp/contracts';
import type { AuditListQuery, AuditResult, SortValue } from '@kp/contracts';
import { dayEnd, dayStart } from '../../app/format.ts';

export type AuditSort = SortValue<(typeof auditSortFields)[number]>;

/** По умолчанию — новые сверху. */
export const DEFAULT_AUDIT_SORT: AuditSort = '-at';

/** Длина строки поиска — как у сервера. */
export const AUDIT_SEARCH_MAX = 200;

export interface AuditSearch {
  /** Часть ФИО автора или названия записи. */
  q?: string;
  actor?: string;
  operation?: string;
  entityType?: string;
  entityId?: string;
  /** Записи о пользователе и его назначениях: «Все →» из карточки (T141). */
  aboutUser?: string;
  /** Первый день периода: 2026-10-14. */
  from?: string;
  /** Последний день периода включительно. */
  to?: string;
  result?: AuditResult;
  /** at — старые сверху, -at — новые сверху; без значения — -at. */
  sort?: AuditSort;
}

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const DATE = /^\d{4}-\d{2}-\d{2}$/;
const CODE = /^[a-z_.]{1,100}$/;

const sortValues: readonly string[] = auditSortFields.flatMap((field) => [field, `-${field}`]);

function pick(value: unknown, pattern: RegExp): string | undefined {
  return typeof value === 'string' && pattern.test(value) ? value : undefined;
}

export function parseAuditSearch(search: Record<string, unknown>): AuditSearch {
  const result: AuditSearch = {};
  if (
    typeof search.q === 'string' &&
    search.q.trim() !== '' &&
    search.q.trim().length <= AUDIT_SEARCH_MAX
  ) {
    result.q = search.q;
  }
  const actor = pick(search.actor, UUID);
  if (actor) result.actor = actor;
  const operation = pick(search.operation, CODE);
  if (operation) result.operation = operation;
  const entityType = pick(search.entityType, CODE);
  if (entityType) result.entityType = entityType;
  const entityId = pick(search.entityId, UUID);
  if (entityId) result.entityId = entityId;
  const aboutUser = pick(search.aboutUser, UUID);
  if (aboutUser) result.aboutUser = aboutUser;
  const from = pick(search.from, DATE);
  if (from) result.from = from;
  const to = pick(search.to, DATE);
  if (to) result.to = to;
  if (search.result === 'ok' || search.result === 'denied') result.result = search.result;
  if (typeof search.sort === 'string' && sortValues.includes(search.sort)) {
    result.sort = search.sort as AuditSort;
  }
  return result;
}

/** Поиск и фильтры сужают список: «Ничего не найдено» вместо «Записей пока нет». */
export function hasAuditFilters(search: AuditSearch): boolean {
  return Object.entries(search).some(
    ([key, value]) => key !== 'sort' && value !== undefined && value !== '',
  );
}

/** Условия запроса к серверу: те же для списка и для выгрузки. */
export function auditFilters(search: AuditSearch, timeZone: string): AuditListQuery {
  const q = search.q?.trim();
  return {
    q: q ? q : undefined,
    actor: search.actor,
    operation: search.operation,
    entityType: search.entityType,
    entityId: search.entityId,
    aboutUser: search.aboutUser,
    from: search.from ? dayStart(search.from, timeZone) : undefined,
    to: search.to ? dayEnd(search.to, timeZone) : undefined,
    result: search.result,
    sort: search.sort ?? DEFAULT_AUDIT_SORT,
  };
}
